import type { PageConfig } from '../types'
import { Rotation } from '../types'

/** Default margin / section height when header or footer is not present (48px) */
export const DEFAULT_SECTION_OR_MARGIN_HEIGHT = 48

export type PageBreakInput = {
  /** Total height of content to split across pages */
  parentHeight: number
  pageDimensions: PageConfig
  /** Whether the page has a header section (replaces top margin) */
  hasHeader: boolean
  /** Whether the page has a footer section (replaces bottom margin) */
  hasFooter: boolean
  /** Measured header height in px; when set, replaces top margin. Fallback: DEFAULT_SECTION_OR_MARGIN_HEIGHT */
  headerHeight?: number
  /** Measured footer height in px; when set, replaces bottom margin. Fallback: DEFAULT_SECTION_OR_MARGIN_HEIGHT */
  footerHeight?: number
}

/**
 * Content height for the first page.
 * Header/footer replace margin: when present we use headerHeight/footerHeight (or default 48), else page margins.
 */
function getFirstPageContentHeight(
  pageHeight: number,
  marginTop: number,
  marginBottom: number,
  hasHeader: boolean,
  hasFooter: boolean,
  headerHeight: number,
  footerHeight: number
): number {
  const top = hasHeader ? headerHeight : marginTop
  const bottom = hasFooter ? footerHeight : marginBottom
  return pageHeight - top - bottom
}

/**
 * Computes the Y positions (in content coordinates) where page breaks should occur
 * so that the number of PDF pages matches the actual printable area, accounting for:
 * - Dynamic header/footer heights (they replace page margin)
 *
 * @returns Array of break positions (cumulative content height at each break)
 */
export function computePageBreakPositions(input: PageBreakInput): number[] {
  const { parentHeight, pageDimensions, hasHeader, hasFooter } = input

  const { dimensions, margins } = pageDimensions
  const pageHeight = dimensions.height
  const marginTop = margins.top
  const marginBottom = margins.bottom

  const headerHeight = input.headerHeight ?? DEFAULT_SECTION_OR_MARGIN_HEIGHT
  const footerHeight = input.footerHeight ?? DEFAULT_SECTION_OR_MARGIN_HEIGHT

  const firstPageContentHeight = getFirstPageContentHeight(
    pageHeight,
    marginTop,
    marginBottom,
    hasHeader,
    hasFooter,
    headerHeight,
    footerHeight
  )

  if (firstPageContentHeight <= 0) return []

  const pageBreak: number[] = []
  let total = firstPageContentHeight

  if (parentHeight <= total) return pageBreak

  pageBreak.push(total)

  while (total < parentHeight) {
    total += firstPageContentHeight
    if (total < parentHeight) pageBreak.push(total)
  }

  return pageBreak
}

/**
 * Same logic for legacy page format (single page height 1056, margins 48).
 * Used when pageVersion <= 1.
 */
export function computePageBreakPositionsLegacy(input: Omit<PageBreakInput, 'pageDimensions'>): number[] {
  const legacyDimensions: PageConfig = {
    dimensions: { width: 816, height: 1056 },
    margins: { top: 48, right: 48, bottom: 48, left: 48 },
    rotation: Rotation.Portrait,
  }
  return computePageBreakPositions({ ...input, pageDimensions: legacyDimensions })
}
